import type {
  CoachMemory,
  CoachMemoryCategory,
  CoachMemoryItem,
} from "./coach-memory";
import { coachMemorySourceLabel } from "./coach-memory";
import { isLegacyF01PreviousAttempt } from "./f01-memory";

// TES-1074 points 2–4 — "Patienten i korthet": the coach's summary, before
// Patientminnet.
//
// A-01: "Den ska ge en snabb, saklig och källspårbar bild av patientens mål, motivation, viktigaste hinder, återkommande mönster, tidigare försök, önskad typ av stöd, vad som hittills fungerat eller inte fungerat samt aktuellt överenskommet fokus."
// "När underlag saknas ska sammanfattningen visa att uppgiften ännu saknas och inte fylla luckan med ett antagande."
//
// So this is a COMPOSITION, not a text generator. Every line is one active
// Patientminnet row, quoted as it stands, with its source and date; nothing is
// rephrased, merged or inferred. The summary therefore can never overwrite
// original data (it writes nothing) and it is exactly as current as the
// memory: a correction or an inactivation there changes it here.
//
// Three rules, each a decision:
//
//  1. An unconfirmed AI observation is a hypothesis, and A-01 forbids filling
//     a gap with an assumption, so it is left out and only counted. Once a
//     coach confirms it, it is a fact and appears.
//  2. "Tidigare försök" is the `previous_attempt` area (TES-1103). An earlier
//     attempt F-01 filed under "Återkommande mönster" before that area
//     existed, and that has not been refiled yet, is recognised by its fixed
//     lead (f01-memory.ts) and shown under "Tidigare försök" too, never
//     twice.
//  3. "Aktuellt överenskommet fokus" is every row whose KIND is an agreement
//     (in any area) plus the agreement area itself, newest first.

export const COACH_BRIEF_SECTIONS = [
  "goal",
  "motivation",
  "obstacles",
  "patterns",
  "previous_attempts",
  "desired_support",
  "worked",
  "did_not_work",
  "agreed_focus",
] as const;
export type CoachBriefSectionKey = (typeof COACH_BRIEF_SECTIONS)[number];

// Headings in A-01's own words where it gives them.
export const COACH_BRIEF_SECTION_LABEL: Record<CoachBriefSectionKey, string> = {
  goal: "Mål",
  motivation: "Motivation",
  obstacles: "Viktigaste hinder",
  patterns: "Återkommande mönster",
  previous_attempts: "Tidigare försök",
  desired_support: "Önskad typ av stöd",
  worked: "Det som fungerat",
  did_not_work: "Det som inte fungerat",
  agreed_focus: "Aktuellt överenskommet fokus",
};

export const COACH_BRIEF_MISSING_TEXT = "Saknas – inget underlag ännu";

export interface CoachBriefLine {
  /** The Patientminnet row this line quotes; the link target. */
  itemId: string;
  body: string;
  /** "Källa" as Patientminnet shows it. */
  sourceLabel: string;
  /** When it was said or observed (not when the row was written). */
  observedAt: string;
  /** True when the row came from F-01, whose original answers can be opened. */
  fromF01: boolean;
}

export interface CoachBriefSection {
  key: CoachBriefSectionKey;
  label: string;
  /** Empty means missing; the surface says so with COACH_BRIEF_MISSING_TEXT. */
  lines: CoachBriefLine[];
}

export interface CoachBrief {
  sections: CoachBriefSection[];
  /** Unconfirmed AI observations left out by rule 1. */
  unconfirmedHypotheses: number;
}

const AREA_OF: Partial<Record<CoachMemoryCategory, CoachBriefSectionKey>> = {
  goal: "goal",
  motivation: "motivation",
  challenge: "obstacles",
  recurring_pattern: "patterns",
  previous_attempt: "previous_attempts",
  desired_support: "desired_support",
  worked: "worked",
  did_not_work: "did_not_work",
  agreement: "agreed_focus",
};

function sectionOf(item: CoachMemoryItem): CoachBriefSectionKey | null {
  if (item.factType === "agreement") return "agreed_focus";
  if (isLegacyF01PreviousAttempt(item)) return "previous_attempts";
  return AREA_OF[item.category] ?? null;
}

// Rule 1, shared with the coach's top summary (coach-summary.ts).
export function isUnconfirmedHypothesis(item: CoachMemoryItem): boolean {
  return item.factType === "ai_observation" && item.confirmedAt === null;
}

/** One memory row as a quoted line with its source and date. */
export function coachBriefLine(item: CoachMemoryItem): CoachBriefLine {
  return {
    itemId: item.id,
    body: item.body,
    sourceLabel: coachMemorySourceLabel(item),
    observedAt: item.observedAt,
    fromF01:
      item.sourceType === "form_submission" && item.sourceTemplateKey === "f01",
  };
}

export function composeCoachBrief(memory: CoachMemory): CoachBrief {
  const buckets = new Map<CoachBriefSectionKey, CoachMemoryItem[]>(
    COACH_BRIEF_SECTIONS.map((k) => [k, []]),
  );
  let unconfirmedHypotheses = 0;
  for (const area of memory.areas) {
    for (const item of area.items) {
      // The read shape lists active rows only; stay safe if that widens.
      if (item.lifecycle !== "active") continue;
      if (isUnconfirmedHypothesis(item)) {
        unconfirmedHypotheses += 1;
        continue;
      }
      const key = sectionOf(item);
      if (key) buckets.get(key)!.push(item);
    }
  }
  return {
    unconfirmedHypotheses,
    sections: COACH_BRIEF_SECTIONS.map((key) => ({
      key,
      label: COACH_BRIEF_SECTION_LABEL[key],
      lines: buckets
        .get(key)!
        .slice()
        .sort((a, b) => b.observedAt.localeCompare(a.observedAt))
        .map(coachBriefLine),
    })),
  };
}

// TES-1211 — the brief as ONE coherent paragraph, deterministically.
//
// A-01 asks for a "sammanhängande" summary, not a row list. Simon
// (2026-10-18): this does not wait for the AI switch, so it is built from
// what is already known and nothing is generated. Each section with
// something to say becomes one sentence: its label as a run-in head, then
// its newest lines quoted as stored (only a closing full stop or similar is
// dropped, so two lines can be joined with "; "). Each line keeps its
// source, so the surface footnotes it back to the Patientminnet row, its
// date and, for F-01, the original answer. A section with nothing is never
// filled in; it is named in one closing sentence instead.
//
// Being a function of the brief, the paragraph stores nothing and is
// recomputed on every read: a correction, inactivation or deletion in
// Patientminnet changes it the next time it is fetched.

/** Lines quoted per sentence; the rest is left to Patientminnet. */
export const COACH_BRIEF_SENTENCE_MAX_LINES = 2;

export const COACH_BRIEF_PARAGRAPH_MISSING_LEAD = "Inget underlag ännu om";

export interface CoachBriefSentence {
  key: CoachBriefSectionKey;
  label: string;
  /** The quoted lines, newest first. Each one is also a source. */
  lines: CoachBriefLine[];
  /** Further rows in the section that are not quoted here. */
  more: number;
}

export interface CoachBriefParagraph {
  sentences: CoachBriefSentence[];
  /** Sections with no basis yet, in A-01's order. */
  missing: { key: CoachBriefSectionKey; label: string }[];
  unconfirmedHypotheses: number;
}

export function coachBriefParagraph(
  brief: CoachBrief,
  maxLines = COACH_BRIEF_SENTENCE_MAX_LINES,
): CoachBriefParagraph {
  const sentences: CoachBriefSentence[] = [];
  const missing: CoachBriefParagraph["missing"] = [];
  for (const section of brief.sections) {
    if (section.lines.length === 0) {
      missing.push({ key: section.key, label: section.label });
      continue;
    }
    sentences.push({
      key: section.key,
      label: section.label,
      lines: section.lines.slice(0, maxLines),
      more: Math.max(0, section.lines.length - maxLines),
    });
  }
  return {
    sentences,
    missing,
    unconfirmedHypotheses: brief.unconfirmedHypotheses,
  };
}

/** A quoted line as a clause: the stored text without its closing mark. */
export function coachBriefClause(body: string): string {
  return body.trim().replace(/[\s.;,:!]+$/u, "");
}

/** The closing sentence that names what is not known yet, or null. */
export function coachBriefMissingSentence(
  missing: CoachBriefParagraph["missing"],
): string | null {
  if (missing.length === 0) return null;
  const names = missing.map((m) => m.label.toLowerCase());
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} och ${names[names.length - 1]}`;
  return `${COACH_BRIEF_PARAGRAPH_MISSING_LEAD} ${list}.`;
}
